/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  ContentType,
} from "@dimensional-engineering/dimensional-models";
import { Roboto_Mono } from "next/font/google";
import html2canvas from "html2canvas";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";

import modalStyles from "./WeeklyVennModal.module.css";
import shareStyles from "./WeeklyVennShareView.module.css";
import WeeklyVennDiagram, {
  getVennPositionLabel,
  VennPeerPoint,
} from "./WeeklyVennDiagram";

import { RootState } from "@/redux/store";
import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import CloseButtonSummary from "@/components/shared/CloseButtonSummary";
import { useModalContext } from "@/context/ModalContext";
import { DailyStoriesInterractionBar } from "@/components/results/Snippets/SnippetInterractionBar/SnippetInterractionBar";
import { Mixpanel } from "@/helpers/mixpanel";
import { useAuth } from "@/context/AuthContext";
import ShareCaptionModal from "../ABSTRACTS/AppHomeSharedTab/ShareCaptionModal/ShareCaptionModal";
import { SharedPostWithCommentThread } from "@/models/sharedModels";
import SharedPostModal from "../ABSTRACTS/AppHomeSharedTab/SharedPostModal/SharedPostModal";
import { AnalysisFeedbackSnippets } from "@/components/results/personalityAnalysis/AnalysisFeedback/AnalysisFeedback";
import { db } from "@/_firebase/firebaseConfig";
import LogoWithText from "@/components/shared/LogoWithText";

const font = Roboto_Mono({ subsets: ["latin"] });

type ActiveTooltip = {
  caption: string;
  positionLabel: string;
  publicProfile: RootState["user"]["publicProfileData"];
  profileImage: string;
  sharedPost: SharedPostWithCommentThread;
};

export default function WeeklyVennModal(props: {
  initialSelectedUserUid?: string;
}) {
  const { setModalOpen, setModalComponent } = useModalContext();
  const { user } = useAuth();

  const { latestVenn, deliveredForLatest } = useSelector(
    (state: RootState) => state.weeklyVenn
  );
  const userPublicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const userImageUrl = useSelector((state: RootState) => state.user.imageURL);
  const sharedPosts = useSelector(
    (state: RootState) => state.sharedPosts
  ).sharedPosts;
  const connectionProfiles = useSelector(
    (state: RootState) => state.connections.publicProfiles
  );

  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [caption, setCaption] = useState("");
  const [selectedFilterKey, setSelectedFilterKey] = useState<string>("all");
  const [activeTooltip, setActiveTooltip] = useState<ActiveTooltip | null>(null);
  const [showRating, setShowRating] = useState(false);
  const [feedbackScore, setFeedbackScore] = useState<number | null>(null);

  const foundPost = useIsWeeklyVennShared(user?.uid, latestVenn?.slug);
  const peerPoints = useWeeklyVennSharedPeerPoints(
    latestVenn?.slug,
    sharedPosts,
    connectionProfiles,
    user?.uid
  );

  const filterUsers = useMemo(() => {
    const users: Array<{ key: string; label: string }> = [];

    if (userPublicProfile?.userName) {
      users.push({
        key: "me",
        label: `@${userPublicProfile.userName}`,
      });
    }

    const seenConnectionKeys = new Set<string>();
    const sortedConnections = [...peerPoints]
      .sort((a, b) => a.userName.localeCompare(b.userName))
      .filter((point) => {
        const key = `connection:${point.authorUid}`;
        if (seenConnectionKeys.has(key)) {
          return false;
        }
        seenConnectionKeys.add(key);
        return true;
      });

    sortedConnections.forEach((point) => {
      users.push({
        key: `connection:${point.authorUid}`,
        label: `@${point.userName}`,
      });
    });

    return users;
  }, [peerPoints, userPublicProfile?.userName]);

  const pointsToRender = useMemo(() => {
    if (selectedFilterKey === "all") {
      return peerPoints;
    }
    if (selectedFilterKey === "me") {
      return [];
    }
    if (selectedFilterKey.startsWith("connection:")) {
      const selectedConnectionUid = selectedFilterKey.replace("connection:", "");
      return peerPoints.filter((point) => point.authorUid === selectedConnectionUid);
    }
    return peerPoints;
  }, [peerPoints, selectedFilterKey]);

  const showMyPoint = selectedFilterKey === "all" || selectedFilterKey === "me";
  const vennTitle = latestVenn?.vennName?.toUpperCase() ?? "WEEKLY VENN";
  const safeImage = userImageUrl || "/default-image.png";

  useEffect(() => {
    if (!props.initialSelectedUserUid) {
      setSelectedFilterKey("all");
      return;
    }
    if (props.initialSelectedUserUid === user?.uid) {
      setSelectedFilterKey("me");
      return;
    }
    setSelectedFilterKey(`connection:${props.initialSelectedUserUid}`);
  }, [props.initialSelectedUserUid, user?.uid]);

  useEffect(() => {
    if (!latestVenn?.slug || !deliveredForLatest) {
      return;
    }
    Mixpanel?.track(
      "Weekly Venn Viewed",
      {
        slug: latestVenn.slug,
      },
      user?.uid
    );
  }, [deliveredForLatest, latestVenn?.slug, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !latestVenn?.slug) {
      setFeedbackScore(null);
      return;
    }

    const deliveredRef = doc(db, "members", user.uid, "weeklyVenns", latestVenn.slug);
    const unsubscribe = onSnapshot(deliveredRef, (snapshot) => {
      if (!snapshot.exists()) {
        setFeedbackScore(null);
        return;
      }
      const score = snapshot.data().feedbackScore;
      if (score === null || score === undefined) {
        setFeedbackScore(null);
        return;
      }
      setFeedbackScore(score as number);
      setShowRating(false);
    });

    return () => {
      unsubscribe();
    };
  }, [latestVenn?.slug, user?.uid]);

  if (!latestVenn || !deliveredForLatest) {
    return null;
  }

  function openSharedPostFromTooltip() {
    if (!activeTooltip) {
      return;
    }
    setModalComponent(
      <SharedPostModal
        profileImage={activeTooltip.profileImage}
        publicProfile={activeTooltip.publicProfile}
        sharedPost={activeTooltip.sharedPost}
      />
    );
  }

  function handleMyPointClick() {
    if (!foundPost) {
      return;
    }
    setActiveTooltip({
      caption: foundPost.caption ?? "",
      positionLabel: getVennPositionLabel(deliveredForLatest!.position),
      publicProfile: userPublicProfile,
      profileImage: safeImage,
      sharedPost: foundPost,
    });
  }

  function handlePeerPointClick(point: VennPeerPoint) {
    const peerPost = sharedPosts.find((post) => {
      return (
        post.postContent.contentIdentifier.contentType === ContentType.weeklyVenn &&
        post.postContent.contentIdentifier.contentSlug === latestVenn!.slug &&
        post.authorUid === point.authorUid
      );
    });

    if (!peerPost) {
      return;
    }

    const trimmedCaption = (peerPost.caption ?? "").trim();
    if (!trimmedCaption) {
      return;
    }

    setActiveTooltip({
      caption: trimmedCaption,
      positionLabel: getVennPositionLabel(point.position),
      publicProfile: point.publicProfile,
      profileImage: point.imageUrl,
      sharedPost: peerPost as SharedPostWithCommentThread,
    });
  }

  function handlePost() {
    if (foundPost) {
      Mixpanel?.track(
        "Post viewed",
        {
          source: "from own results modal",
          content_type: "weekly venn",
          content_type_slug: latestVenn?.slug,
          caption_text: foundPost.caption,
        },
        user?.uid
      );
      return setModalComponent(
        <SharedPostModal
          profileImage={safeImage}
          publicProfile={userPublicProfile}
          sharedPost={foundPost}
        />
      );
    }

    return setShowCaptionModal(true);
  }

  async function handleRating(value: number) {
    if (!user?.uid || !latestVenn?.slug) {
      return;
    }

    await updateDoc(doc(db, "members", user.uid, "weeklyVenns", latestVenn.slug), {
      feedbackScore: value,
    });

    setFeedbackScore(value);
    setShowRating(false);

    Mixpanel?.track(
      "Feedback given",
      {
        slug: latestVenn.slug,
        score: value,
        content_type: "weekly_venn",
      },
      user?.uid
    );
  }

  return (
    <>
      {showCaptionModal && (
        <ShareCaptionModal
          contentIdentifier={{
            contentSlug: latestVenn.slug,
            contentType: ContentType.weeklyVenn,
          }}
          onClose={(captionText) => {
            setCaption(captionText);
            setShowCaptionModal(false);
          }}
          savedCaption={caption}
        />
      )}
      <ModalWrapper title="" noHeader noLine>
        <main className={modalStyles.main} onClick={() => setActiveTooltip(null)}>
          <button
            type="button"
            className={modalStyles.closeButton}
            onClick={() => setModalOpen(false)}
          >
            <CloseButtonSummary />
          </button>

          <h2 className={`${modalStyles.title} ${font.className}`}>{vennTitle}</h2>

          <p className={modalStyles.topLabel}>
            {capitalizeFirstLetterOnly(latestVenn.topCircleLabel)}
          </p>

          <div className={modalStyles.diagramWrap}>
            <WeeklyVennDiagram
              topLabel={latestVenn.topCircleLabel}
              leftLabel={latestVenn.leftCircleLabel}
              rightLabel={latestVenn.rightCircleLabel}
              myPoint={deliveredForLatest}
              myImageUrl={safeImage}
              myPublicProfile={userPublicProfile}
              peerPoints={pointsToRender}
              showMyPoint={showMyPoint}
              showLabels={false}
              layout="modal"
              limitSections
              onMyPointClick={handleMyPointClick}
              onPeerPointClick={handlePeerPointClick}
            />
            {activeTooltip && (
              <button
                type="button"
                className={modalStyles.captionTooltip}
                onClick={(event) => {
                  event.stopPropagation();
                  openSharedPostFromTooltip();
                }}
              >
                {activeTooltip.caption ||
                  `${activeTooltip.positionLabel}: view shared post`}
              </button>
            )}
          </div>

          <div className={modalStyles.bottomLabels}>
            <p>{capitalizeFirstLetterOnly(latestVenn.leftCircleLabel)}</p>
            <p>{capitalizeFirstLetterOnly(latestVenn.rightCircleLabel)}</p>
          </div>

          <div className={modalStyles.filterRow}>
            <span className={modalStyles.showLabel}>Show:</span>
            <button
              type="button"
              className={
                selectedFilterKey === "all" ? modalStyles.filterActive : modalStyles.filter
              }
              onClick={() => setSelectedFilterKey("all")}
            >
              All
            </button>
            {filterUsers.map((filterUser) => (
              <button
                type="button"
                key={filterUser.key}
                className={
                  selectedFilterKey === filterUser.key
                    ? modalStyles.filterActive
                    : modalStyles.filter
                }
                onClick={() => setSelectedFilterKey(filterUser.key)}
              >
                {filterUser.label}
              </button>
            ))}
          </div>

          <div className={modalStyles.interactionBar}>
            {showRating && (
              <div className={modalStyles.feedbackDiv}>
                <AnalysisFeedbackSnippets
                  onAnswer={(value) => {
                    handleRating(value);
                  }}
                  title="How good was this content?"
                  subtitle="Feedback improves your results"
                  currentAnswer={feedbackScore}
                  onClose={() => setShowRating(false)}
                />
              </div>
            )}
            <DailyStoriesInterractionBar
              onRateClick={() => {
                setActiveTooltip(null);
                setShowRating(!showRating);
              }}
              onCommentClick={() => {}}
              onGoDeeper={() => {}}
              onPostClick={() => {
                Mixpanel?.track(
                  "Post to connections CTA pressed",
                  {
                    content_type: "weekly venn",
                    slug: latestVenn.slug,
                  },
                  user?.uid
                );
                handlePost();
              }}
              posted={!!foundPost}
              commentCount={0}
              onShare={(type) => {
                Mixpanel?.track(
                  "Shared to Social",
                  {
                    platform: type === "instagram" ? "Instagram" : "Snapchat",
                    type: "weekly venn",
                    slug: latestVenn.slug,
                  },
                  user?.uid
                );
                setModalComponent(<WeeklyVennShareView type={type} />);
              }}
            />
          </div>
        </main>
      </ModalWrapper>
    </>
  );
}

export function WeeklyVennShareView(props: { type: "instagram" | "snapchat" }) {
  const { setModalOpen } = useModalContext();
  const imageRef = useRef<HTMLElement>(null);

  const { latestVenn, deliveredForLatest } = useSelector(
    (state: RootState) => state.weeklyVenn
  );
  const userPublicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const userImageUrl = useSelector((state: RootState) => state.user.imageURL);

  useEffect(() => {
    const handleDownloadImage = async () => {
      if (!imageRef.current) {
        return;
      }
      const canvas = await html2canvas(imageRef.current);
      const data = canvas.toDataURL("image/jpg");

          //       var a = document.createElement("a");
          // a.href = canvas
          //   .toDataURL("image/jpeg")
          //   .replace("image/jpeg", "image/octet-stream");
          // a.download = "somefilename.jpg";
          // a.click();

      if (window.Android) {
        window.Android.onHaptic();
        if (props.type === "instagram") {
          window.Android.shareToInstagramQuiz(data);
        } else {
          window.Android.shareToSnapChat(data);
        }
      }
    };

    setTimeout(() => {
      handleDownloadImage();
      setModalOpen(false);
    }, 110);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!latestVenn || !deliveredForLatest) {
    return null;
  }

  return (
    <ModalWrapper title="" noHeader noLine>
      <main ref={imageRef} className={shareStyles.main}>
        <div className={shareStyles.logo}>
          <LogoWithText />
        </div>
        <h2 className={`${shareStyles.title} ${font.className}`}>
          {latestVenn.vennName.toUpperCase()}
        </h2>
        <p className={shareStyles.topLabel}>
          {capitalizeFirstLetterOnly(latestVenn.topCircleLabel)}
        </p>
        <div className={shareStyles.diagramWrap}>
          <WeeklyVennDiagram
            topLabel={latestVenn.topCircleLabel}
            leftLabel={latestVenn.leftCircleLabel}
            rightLabel={latestVenn.rightCircleLabel}
            myPoint={deliveredForLatest}
            myImageUrl={userImageUrl || "/default-image.png"}
            myPublicProfile={userPublicProfile}
            peerPoints={[]}
            avatarSize="medium"
            layout="socialShare"
            showLabels={false}
          />
        </div>
        <div className={shareStyles.bottomLabels}>
          <p>{capitalizeFirstLetterOnly(latestVenn.leftCircleLabel)}</p>
          <p>{capitalizeFirstLetterOnly(latestVenn.rightCircleLabel)}</p>
        </div>
      </main>
    </ModalWrapper>
  );
}

function useIsWeeklyVennShared(
  userUid: string | undefined,
  vennSlug: string | undefined
) {
  const sharedPosts = useSelector(
    (state: RootState) => state.sharedPosts
  ).sharedPosts;

  const [foundPost, setFoundPost] =
    useState<SharedPostWithCommentThread | null>(null);

  useEffect(() => {
    const slug = `${userUid}-weeklyVenn-${vennSlug}`;
    const isFound = sharedPosts.find((post) => post.slug === slug);
    setFoundPost((isFound as SharedPostWithCommentThread) ?? null);
  }, [vennSlug, sharedPosts, userUid]);

  return foundPost;
}

function useWeeklyVennSharedPeerPoints(
  vennSlug: string | undefined,
  sharedPosts: RootState["sharedPosts"]["sharedPosts"],
  connectionProfiles: RootState["connections"]["publicProfiles"],
  userUid: string | undefined
) {
  return useMemo(() => {
    if (!vennSlug) {
      return [];
    }

    const postsForVenn = sharedPosts.filter((post) => {
      return (
        post.postContent.contentIdentifier.contentType === ContentType.weeklyVenn &&
        post.postContent.contentIdentifier.contentSlug === vennSlug &&
        post.authorUid !== userUid &&
        post.postContent.weeklyVennData?.deliveredWeeklyVenn
      );
    });

    return postsForVenn
      .sort((a, b) => b.createdOn - a.createdOn)
      .map((post) => {
        const connection = connectionProfiles.find(
          (profile) => profile.ownerUUID === post.authorUid
        );
        const delivered = post.postContent.weeklyVennData?.deliveredWeeklyVenn;

        if (!connection || !delivered) {
          return null;
        }

        return {
          authorUid: post.authorUid,
          position: delivered.position,
          imageUrl: connection.imageUrl,
          userName: connection.userName,
          publicProfile: connection,
          createdOn: post.createdOn,
        } as VennPeerPoint;
      })
      .filter((point): point is VennPeerPoint => !!point);
  }, [vennSlug, connectionProfiles, sharedPosts, userUid]);
}

function capitalizeFirstLetterOnly(label: string) {
  if (!label) {
    return label;
  }

  return `${label.charAt(0).toUpperCase()}${label.slice(1).toLowerCase()}`;
}
